import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import GameDetailView from '../components/GameDetailView';
import './GamePage.css';

const categories = ["Motor Skills", "Cognitive", "Precision", "Memory", "Therapeutic", "Perception", "Creative", "Educational", "Life Skills"];

const GamesPage = ({ games, isLoading, onGameSelect }) => {
  const [selectedCategories, setselectedCategories] = useState([]);
  const [hoveredGame, setHoveredGame] = useState(null);
  const [playerCount, setPlayerCount] = useState(1);
  const [robotSupport, setRobotSupport] = useState(null);
  const [cognitiveDifficulty, setCognitiveDifficulty] = useState(4);
  const [motorDifficulty, setMotorDifficulty] = useState(4);
  const [searchParams, setSearchParams] = useSearchParams();

  const name = searchParams.get("name");
  const game = games.find(g => g.name === name);
  const isEmbedded = searchParams.get("embed") === "1";

  // Filter games based on selection
  const filteredGames = useMemo(() => {
    const filteredGameSpecs = games.filter(game =>
      (game.robotSupport === robotSupport || robotSupport === null) &&
      game.playerCount === playerCount && 
      game.cognitiveDifficulty <= cognitiveDifficulty &&
      game.motorDifficulty <= motorDifficulty
    );

    if (selectedCategories.length === 0) return filteredGameSpecs;

    return filteredGameSpecs.filter(game => selectedCategories.includes(game.category));
  }, [selectedCategories, robotSupport, playerCount, cognitiveDifficulty, motorDifficulty, games]);

  const handleGameClick = (game) => {
   if (game) setSearchParams({ name: game.name});
   //if (game) navigate(`/games/${game.name}`);
  };

  const handleCategoryClick = (category) => {
    if (category === "All") setselectedCategories([]);
    else if (selectedCategories.includes(category)) setselectedCategories(selectedCategories.filter(cat => cat !== category));
    else setselectedCategories([...selectedCategories, category]);
  };

  const handleRobotSupportClick = (option) => {
    if (robotSupport === option) setRobotSupport(null);
    else setRobotSupport(option);
  };

  // Launcher webview: render the game on its own, with no header, filters or
  // grid around it. Placed after every hook above so the hook order is stable.
  if (isEmbedded) {
    if (isLoading) {
      return (
        <div className="games-page-embed">
          <div className="loading-spinner"></div>
        </div>
      );
    }

    if (!game) {
      return (
        <div className="games-page-embed">
          <p className="embed-message">
            {name ? `No game found named “${name}”.` : 'No game was specified.'}
          </p>
        </div>
      );
    }

    return <GameDetailView game={game} embedded />;
  }

return (
  <div className="games-page-light">
    { game && (
    <div>
      <GameDetailView game={game}/>
    </div>)}

    {/* Floating Gradient Orbs */}
    <div className="floating-orbs">
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>
      <div className="orb orb-3"></div>
      <div className="orb orb-4"></div>
      <div className="orb orb-5"></div>
    </div>
    
    <div className="games-container">
      {/* Header */}
      <motion.div 
        className="games-header"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        >
        <h1 className="games-title">
          Fitness Games for <span>People with Disabilities</span>
        </h1>
        <p className="games-subtitle">
          Explore our collection of therapeutic games designed to provide accessible 
          recreation and exercise for individuals with disabilities
        </p>
      </motion.div>

      <div className="games-body">
        {/* Category Filter */}
        <motion.div 
          className="filters-section"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
          >
          <div className="category-filter">
            <button
              className={`category-btn ${selectedCategories.length === 0 ? 'active' : ''}`}
              onClick={() => handleCategoryClick("All")}>
              All
            </button>
            {categories.map(category => (
              <button
                key={category}
                className={`category-btn ${selectedCategories.includes(category) ? 'active' : ''}`}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </button>
            ))}
          </div>
          
          <div className="gameSpecsFilter">
            <p>Robot Support?</p>
            <button
              className={`robotSupportBtn ${robotSupport === true ? 'active' : ''}`}
              onClick={() => handleRobotSupportClick(true)}>
              Yes
            </button>
            <button
              className={`robotSupportBtn ${robotSupport === false ? 'active' : ''}`}
              onClick={() => handleRobotSupportClick(false)}>
              No
            </button>
            
            <div className="game-spec">
              <p>Player Count: <span>{playerCount}</span></p>
              <input
              type="range" min="1" max="2" value={playerCount}
              onChange={(e) => setPlayerCount(Number(e.target.value))}></input>
            </div>
            <div className="game-spec">
              <p>Cognitive Difficulty: <span>{cognitiveDifficulty}</span></p>
              <input
              type="range" min="1" max="4" value={cognitiveDifficulty}
              onChange={(e) => setCognitiveDifficulty(Number(e.target.value))}></input>
            </div>
            <div className="game-spec">
              <p>Motor Difficulty: <span>{motorDifficulty}</span></p>
              <input
              type="range" min="1" max="4" value={motorDifficulty}
              onChange={(e) => setMotorDifficulty(Number(e.target.value))}></input>
            </div>
          </div>
        </motion.div>

        {/* Games Grid */}
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : filteredGames.length === 0 ? (
          <div className="no-games">
            <h3>No games found</h3>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div 
              className="games-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {filteredGames.map((game, index) => (
                <motion.div
                  key={game.id}
                  className="game-card-light"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onMouseEnter={() => setHoveredGame(game.id)}
                  onMouseLeave={() => setHoveredGame(null)}
                  onClick={() => handleGameClick(game)}
                >
                  <div className="game-image-container">
                    <img 
                      src={game.thumbnail} 
                      alt={game.name}
                      className="game-thumbnail"
                      onError={(e) => {
                        e.target.src = `https://via.placeholder.com/400x300/6366f1/ffffff?text=${game.name}`;
                      }}
                    />
                    <div className="play-overlay">
                      <div className="play-btn-circle">▶</div>
                    </div>
                    <div className={`difficulty-badge ${game.difficulty.toLowerCase()}`}>
                      {game.difficulty}
                    </div>
                  </div>

                  <div className="game-content">
                    <h3 className="game-title">{game.name}</h3>
                    <p className="game-description">{game.description}</p>
                    
                    <div className="game-meta-row">
                      <div className="game-tags">
                        <span className="game-category">{game.category}</span>
                      </div>
                      <span className="game-duration">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M8 4.5V8L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        {game.duration}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
      </div>
    </div>
  );
};

export default GamesPage;